/**
 * Web Push in this browser (docs/specs/cloud/alerts.md §5): the service worker `/teslyn-sw.js`, the permission and
 * the subscription with the organisation's public key. The browser's objects are passed in (`PushEnv`) so the screen
 * tests use fakes; nothing here reads a key other than the public one.
 */

export const SERVICE_WORKER_URL = '/teslyn-sw.js';

/** The parts of a push subscription Teslyn stores (`PushSubscription.toJSON()`). */
export interface PushJson {
  readonly endpoint: string;
  readonly p256dh: string;
  readonly auth: string;
}

interface SubscriptionLike {
  readonly endpoint: string;
  toJSON(): { endpoint?: string; keys?: Record<string, string> };
  unsubscribe(): Promise<boolean>;
}

interface RegistrationLike {
  readonly pushManager: {
    getSubscription(): Promise<SubscriptionLike | null>;
    subscribe(o: { userVisibleOnly: boolean; applicationServerKey: Uint8Array }): Promise<SubscriptionLike>;
  };
}

/** The browser's service worker container and notification permission, or fakes. */
export interface PushEnv {
  readonly serviceWorker: {
    register(url: string, o?: { scope?: string }): Promise<RegistrationLike>;
    getRegistration(url?: string): Promise<RegistrationLike | undefined>;
  } | null;
  readonly permission: () => 'default' | 'granted' | 'denied';
  readonly requestPermission: () => Promise<'default' | 'granted' | 'denied'>;
  readonly hasPushManager: boolean;
  readonly userAgent: string;
}

/** This browser's real objects (null service worker where there is none: no push). */
export function browserPushEnv(): PushEnv {
  const nav = typeof navigator === 'undefined' ? null : navigator;
  const N = typeof Notification === 'undefined' ? null : Notification;
  return {
    serviceWorker:
      nav && 'serviceWorker' in nav ? (nav.serviceWorker as unknown as PushEnv['serviceWorker']) : null,
    permission: () => (N ? N.permission : 'denied'),
    requestPermission: () => (N ? N.requestPermission() : Promise.resolve('denied')),
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    userAgent: nav?.userAgent ?? '',
  };
}

export type PushSupport = 'ready' | 'unsupported' | 'blocked';

/** Whether this browser can take push notices: a service worker and push, and notifications not blocked. */
export function pushSupport(env: PushEnv): PushSupport {
  if (!env.serviceWorker || !env.hasPushManager) return 'unsupported';
  return env.permission() === 'denied' ? 'blocked' : 'ready';
}

/** base64url (the public key as Teslyn stores it) to the bytes `applicationServerKey` takes. */
export function keyBytes(b64url: string): Uint8Array {
  const t = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(t + '='.repeat((4 - (t.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

function toJson(s: SubscriptionLike): PushJson {
  const j = s.toJSON();
  const p256dh = j.keys?.p256dh ?? '';
  const auth = j.keys?.auth ?? '';
  if (!j.endpoint || !p256dh || !auth) throw new Error('The browser gave an incomplete push subscription.');
  return { endpoint: j.endpoint, p256dh, auth };
}

/** This browser's current subscription, if it has one. */
export async function currentPush(env: PushEnv): Promise<PushJson | null> {
  if (!env.serviceWorker) return null;
  const reg = await env.serviceWorker.getRegistration(SERVICE_WORKER_URL);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return sub ? toJson(sub) : null;
}

/** Ask for permission, register the service worker and subscribe with the public key. */
export async function turnOnPush(env: PushEnv, publicKey: string): Promise<PushJson> {
  if (pushSupport(env) === 'unsupported' || !env.serviceWorker)
    throw new Error('This browser cannot receive push alerts.');
  const permission = await env.requestPermission();
  if (permission !== 'granted')
    throw new Error(
      'Notifications are blocked for this site. Allow them in the browser’s site settings, then try again.',
    );
  const reg = await env.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  return toJson(sub);
}

/** Unsubscribe this browser; its endpoint, so the stored subscription can be removed too. */
export async function turnOffPush(env: PushEnv): Promise<string | null> {
  if (!env.serviceWorker) return null;
  const reg = await env.serviceWorker.getRegistration(SERVICE_WORKER_URL);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}

/** "Chrome on Android", "Safari on iPhone", "Edge on Windows": a device's name in the list. */
export function deviceLabel(ua: string): string {
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Mac OS X|Macintosh/.test(ua)
            ? 'Mac'
            : /Linux/.test(ua)
              ? 'Linux'
              : '';
  return os ? `${browser} on ${os}` : browser;
}

/** `?page=alerts&alert=<id>`: a notice opened from a phone or an email. */
export function alertsLink(search: string): { readonly open: boolean; readonly alertId: string | null } {
  const q = new URLSearchParams(search);
  const open = q.get('page') === 'alerts';
  const id = q.get('alert');
  return { open, alertId: open && id && /^[0-9a-f-]{36}$/i.test(id) ? id : null };
}
